import React, { KeyboardEvent, MouseEvent, useEffect, useRef } from 'react';

import Icon from '@/components/ui/Icon';
import { COLORS } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';
import styles from './Checkbox.module.scss';

// Check and Minus share a viewBox and stroke thickness, so one size keeps the checked and
// partial glyphs at the same weight. 8.5 puts them at the Figma Small variant's 8px wide.
const GLYPH_PX = 8.5;

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode;
  extraClassName?: string;
  extraInputClassName?: string;
  extraLabelClassName?: string;
  indeterminate?: boolean;
  shouldCover?: boolean;
}

const Checkbox = ({
  label,
  extraClassName,
  extraInputClassName,
  extraLabelClassName,
  indeterminate = false,
  shouldCover,
  ...otherProps
}: Props) => {
  const checkboxRef = useRef<HTMLInputElement>(null);
  const TagName = shouldCover ? 'label' : 'div';

  // Give the input an accessible name from its visible text (WCAG 1.1.1 / 1.3.1 / 4.1.2).
  // Only string labels can name the input; a consumer-supplied aria-label still wins via otherProps.
  const ariaLabel = typeof label === 'string' ? label : undefined;

  const handleClick = (e: MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  // AIDEV-NOTE: Treat Enter and Space as toggle keys. Space is normally native
  // for checkboxes but bubbles to ancestor keydown handlers (e.g. Collapse) that
  // can swallow it; routing through .click() guarantees the toggle happens.
  // AIDEV-NOTE: Skip auto-repeat; a held key toggled ~25x/s and flooded library filter fetches (PLAT-18305).
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    e.preventDefault();
    if (!e.repeat) checkboxRef.current?.click();
  };

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <TagName className={cn(styles.container, 'tw-font-tci', extraClassName)}>
      {/* The glyphs are siblings toggled by CSS, not React, so an uncontrolled checkbox still
          shows its tick — the component never sees the checked state in that case. */}
      <span className={styles.box}>
        <input
          ref={checkboxRef}
          aria-label={ariaLabel}
          className={cn(styles.checkbox, extraInputClassName)}
          type="checkbox"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          {...otherProps}
        />
        <Icon className={styles.check} fill={COLORS.white} icon="Check" size={GLYPH_PX} />
        <Icon className={styles.minus} fill={COLORS.white} icon="Minus" size={GLYPH_PX} />
      </span>
      <span className={cn(styles.label, extraLabelClassName)}>{label}</span>
    </TagName>
  );
};

export default Checkbox;
